// src/views/Interest.jsx
// Pipeline interest levels (Reece, 10/9/26) — the shared UI over
// src/interest.js:
//   InterestMenu  the dot by a file's name: one click opens a compact menu —
//                 Normal · Feel good · On the fence · Don't feel good (one
//                 click sets it and closes) and a Booked switch (one click,
//                 stays open). Keyboard: Enter / Space / ↓ open it, ↑ ↓ Home
//                 End move, Enter / Space pick, Esc closes back to the dot.
//                 It never opens the file or starts a drag (clicks / presses
//                 stop here; LoanRow refuses a drag that starts on it). The
//                 menu is position:fixed — the stage lanes clip overflow — and
//                 PORTALED to document.body with its click-outside overlay: a
//                 row with opacity < 1 (an archived row, Show Archived on) is
//                 its own stacking context, so a menu rendered inside it sat
//                 UNDER the rows below and a click on "Feel good" opened the
//                 next file. React events still bubble through the portal to
//                 this control, which stops them before the row sees them.
//   InterestBar   the Pipeline's summary + filter bar: count and est. comp per
//                 level and Booked; click to filter (multi-select), Clear,
//                 and a one-line key to the colours.
//   BookedLeadsToggle / BookedStrip (10/10/26) — the Pipeline header's
//                 "Booked leads (N)" switch and the "Showing booked leads
//                 only · Show everything" strip; both drive the SAME
//                 filter.booked as the Booked chip.
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { C } from '../theme';
import { INTEREST_LEVELS, INTEREST_META, INTEREST_NORMAL_LABEL, INTEREST_NONE_LABEL, BOOKED_LABEL, interestStyle, interestFilterActive } from '../interest';

const FONT="'Mulish',system-ui,sans-serif";
const MENU_W=244;
const stop=function(e){e.stopPropagation();};

// The dot itself: filled in the feeling's colour (a dashed ring when nothing
// is set), with a yellow halo when the file is booked.
function InterestDot({loan,size}){
  const st=interestStyle(loan);
  const sz=size||12;
  return(<span aria-hidden="true" style={{display:"inline-block",width:sz,height:sz,borderRadius:"50%",boxSizing:"border-box",flexShrink:0,
    background:st.color||"transparent",border:st.color?"2px solid "+st.color:"2px dashed "+C.textDim,
    boxShadow:st.booked?"0 0 0 3px "+C.booked+", 0 0 0 4px "+C.bookedEdge:"none"}}/>);
}

function InterestMenu({loan,onSet,size,onOpenChange,align}){
  const [open,setOpen]=useState(false);
  const [pos,setPos]=useState(null);
  const btnRef=useRef(null);
  const menuRef=useRef(null);
  const onOpenChangeRef=useRef(onOpenChange);onOpenChangeRef.current=onOpenChange;
  const st=interestStyle(loan);
  const name=(loan&&(loan.b||loan.id))||"this file";
  const anchorRef=useRef(null);
  const place=function(){
    const b=btnRef.current;
    const r=b&&b.getBoundingClientRect?b.getBoundingClientRect():{left:0,right:0,top:0,bottom:0};
    anchorRef.current={left:r.left,top:r.top};
    const vw=window.innerWidth||1024;
    const left=align==="right"?r.right-MENU_W:r.left;
    return{left:Math.max(8,Math.min(left,vw-MENU_W-8)),top:r.bottom+6,above:r.top};
  };
  const setOpenState=function(v,focusBtn){
    setOpen(v);
    if(v)setPos(place());
    if(onOpenChangeRef.current)onOpenChangeRef.current(v);
    if(!v&&focusBtn&&btnRef.current)try{btnRef.current.focus();}catch(e){/* ignore */}
  };
  // Flip above the dot when the menu would run off the bottom of the window.
  useLayoutEffect(function(){
    if(!open||!pos||!menuRef.current)return;
    const h=menuRef.current.offsetHeight||0;
    const vh=window.innerHeight||768;
    if(h&&pos.top+h>vh-8&&pos.above-6-h>=8)setPos(Object.assign({},pos,{top:pos.above-6-h,above:-1e9}));
  },[open,pos]);
  // The dot moved while the menu is open (its row re-ranked, a row above it
  // left the lane): the menu follows the dot — it never stays over the row
  // that took its place. (The Pipeline also holds a lane's order while one
  // of its menus is open, so normally nothing moves.) Every render on
  // purpose; it only sets when the dot really moved (place() re-anchors).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(function(){
    if(!open||!pos||!anchorRef.current)return;
    const b=btnRef.current;
    if(!b||!b.getBoundingClientRect)return;
    const r=b.getBoundingClientRect();
    if(Math.abs(r.left-anchorRef.current.left)>0.5||Math.abs(r.top-anchorRef.current.top)>0.5)setPos(place());
  });
  // Focus the current choice when it opens; a scroll or resize closes it
  // (it is fixed to where the dot WAS).
  useEffect(function(){
    if(!open)return undefined;
    const m=menuRef.current;
    if(m){const cur=m.querySelector('[aria-checked="true"][data-interest-pick]')||m.querySelector('[data-interest-pick]');if(cur)try{cur.focus();}catch(e){/* ignore */}}
    const close=function(){setOpenState(false,false);};
    window.addEventListener("resize",close);
    window.addEventListener("scroll",close,true);
    return function(){window.removeEventListener("resize",close);window.removeEventListener("scroll",close,true);};
  },[open]);// eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the screen with it open still tells the row it closed.
  useEffect(function(){return function(){if(onOpenChangeRef.current&&open)onOpenChangeRef.current(false);};},[open]);
  const items=function(){return menuRef.current?Array.from(menuRef.current.querySelectorAll('[role^="menuitem"]')):[];};
  const onMenuKey=function(e){
    e.stopPropagation();
    const list=items();const i=list.indexOf(document.activeElement);
    const go=function(n){e.preventDefault();const el=list[(n+list.length)%list.length];if(el)el.focus();};
    if(e.key==="ArrowDown")go(i+1);
    else if(e.key==="ArrowUp")go(i<0?list.length-1:i-1);
    else if(e.key==="Home")go(0);
    else if(e.key==="End")go(list.length-1);
    else if(e.key==="Escape"){e.preventDefault();setOpenState(false,true);}
    // Tab: back on the dot first, so the browser's own Tab moves on from
    // there (the menu lives at the end of <body>, not next to the dot).
    else if(e.key==="Tab")setOpenState(false,true);
  };
  const pick=function(level){if(onSet)onSet({interest:level});setOpenState(false,true);};
  const toggleBooked=function(){if(onSet)onSet({booked:!st.booked});};
  const choices=[{k:null,label:INTEREST_NORMAL_LABEL,color:null}].concat(INTEREST_LEVELS.map(function(k){return{k:k,label:INTEREST_META[k].label,color:INTEREST_META[k].color};}));
  const itemStyle=function(on,color){return{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"8px 10px",borderRadius:7,border:"none",
    background:on?C.accentGlow:"transparent",color:color||C.text,fontSize:13,fontWeight:on?800:700,cursor:"pointer",textAlign:"left",fontFamily:FONT};};
  return(<span data-interest-control={loan&&loan.id} onClick={stop} onMouseDown={stop} onDoubleClick={stop} onKeyDown={stop}
    draggable={false} onDragStart={function(e){e.preventDefault();e.stopPropagation();}}
    style={{position:"relative",display:"inline-flex",alignItems:"center",flexShrink:0}}>
    <button ref={btnRef} type="button" data-interest-btn={loan&&loan.id} aria-haspopup="menu" aria-expanded={open}
      aria-label={"Interest for "+name+": "+(st.label||INTEREST_NORMAL_LABEL)+". Change"}
      title={(st.label?st.label+" — ":"")+"How do you feel about this deal? Click to set (and Booked)"}
      onClick={function(){setOpenState(!open,false);}}
      onKeyDown={function(e){if(e.key==="ArrowDown"&&!open){e.preventDefault();setOpenState(true,false);}}}
      style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,padding:0,margin:"-4px 0",borderRadius:"50%",border:"none",background:open?C.accentGlow:"transparent",cursor:"pointer"}}>
      <InterestDot loan={loan} size={size}/>
    </button>
    {open&&pos&&typeof document!=="undefined"&&document.body&&createPortal(<div data-interest-layer={loan&&loan.id} onClick={stop} onMouseDown={stop} onDoubleClick={stop} onKeyDown={stop}
      draggable={false} onDragStart={function(e){e.preventDefault();e.stopPropagation();}}>
      <div data-interest-overlay="1" onClick={function(e){e.stopPropagation();setOpenState(false,false);}} onMouseDown={stop}
        style={{position:"fixed",inset:0,zIndex:950,background:"transparent",cursor:"default"}}/>
      <div ref={menuRef} role="menu" data-interest-menu={loan&&loan.id} aria-label={"Interest for "+name} onKeyDown={onMenuKey}
        style={{position:"fixed",left:pos.left,top:pos.top,zIndex:951,width:MENU_W,boxSizing:"border-box",background:C.card,border:"1px solid "+C.border,borderRadius:12,boxShadow:"0 12px 32px rgba(6,26,48,0.24)",padding:6,cursor:"default",textAlign:"left"}}>
        <div style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6,padding:"4px 8px 6px"}}>How do you feel about it?</div>
        {choices.map(function(o){
          const on=st.level===o.k;
          return <button key={o.k||"normal"} type="button" role="menuitemradio" aria-checked={on} data-interest-pick={o.k||"normal"} tabIndex={-1}
            onClick={function(){pick(o.k);}} style={itemStyle(on,o.color)}>
            <span aria-hidden="true" style={{width:10,height:10,borderRadius:"50%",flexShrink:0,boxSizing:"border-box",background:o.color||"transparent",border:o.color?"none":"2px dashed "+C.textDim}}/>
            <span style={{flex:1}}>{o.label}</span>
            {on&&<span aria-hidden="true" style={{color:C.text,fontWeight:800}}>✓</span>}
          </button>;
        })}
        <div style={{height:1,background:C.border,margin:"6px 4px"}}/>
        <button type="button" role="menuitemcheckbox" aria-checked={st.booked} data-interest-booked="1" tabIndex={-1} onClick={toggleBooked}
          style={Object.assign(itemStyle(false,C.text),{background:st.booked?C.booked:"transparent",alignItems:"flex-start"})}>
          <span aria-hidden="true" style={{position:"relative",width:30,height:17,borderRadius:10,flexShrink:0,marginTop:1,background:st.booked?C.feelGood:C.cream2,transition:"background .15s"}}>
            <span style={{position:"absolute",top:2,left:st.booked?15:2,width:13,height:13,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 2px rgba(0,0,0,0.25)",transition:"left .15s"}}/>
          </span>
          <span style={{flex:1,minWidth:0}}>
            <span style={{display:"block"}}>{BOOKED_LABEL}</span>
            <span style={{display:"block",fontSize:10.5,fontWeight:500,color:C.textMuted,lineHeight:1.35,marginTop:1}}>Yellow highlight. Your switch — moving stages never changes it.</span>
          </span>
        </button>
      </div>
    </div>,document.body)}
  </span>);
}

const $k=function(n){return"$"+Math.round(Number(n)||0).toLocaleString();};

// The Pipeline's "see what's going on" bar. `summary` = interestSummary(pool)
// (counts never move with the filter itself); `filter` / `onToggle(key)` /
// `onClear()`; `shown` / `total` = files on the board after / before it (and
// the search) — the file call back cards in the Prospect lane included.
function InterestBar({summary,filter,onToggle,onClear,shown,total}){
  const s=summary||{good:{n:0,comm:0},fence:{n:0,comm:0},bad:{n:0,comm:0},none:{n:0,comm:0},booked:{n:0,comm:0}};
  const f=filter||{levels:[],booked:false};
  const active=interestFilterActive(f);
  const chip=function(key,label,color,bg,n,comm){
    const on=key==="booked"?f.booked:f.levels.indexOf(key)>=0;
    return <button key={key} type="button" data-interest-chip={key} aria-pressed={on} onClick={function(){onToggle(key);}}
      title={key==="booked"?"Show only booked files"+(f.levels.length?" (with the level picked)":"")+" — click again to undo":"Show "+label.toLowerCase()+" files — pick more than one to see them together"}
      style={{display:"inline-flex",alignItems:"center",gap:7,padding:"5px 11px",borderRadius:20,cursor:"pointer",fontFamily:FONT,fontSize:12,fontWeight:800,whiteSpace:"nowrap",
        border:(on?"2px solid ":"1px solid ")+(on?(color||C.accent):C.border),margin:on?0:1,
        background:bg||(on?C.accentGlow:C.card),color:color||C.textMuted,boxShadow:on?"0 1px 4px rgba(11,42,74,0.16)":"none"}}>
      {key!=="booked"&&<span aria-hidden="true" style={{width:9,height:9,borderRadius:"50%",boxSizing:"border-box",background:color&&key!=="none"?color:"transparent",border:key==="none"?"2px dashed "+C.textDim:"none"}}/>}
      {label}
      <span data-interest-count={key} style={{fontWeight:800}}>{n}</span>
      <span style={{fontWeight:600,opacity:.8}}>{$k(comm)}</span>
      {on&&<span aria-hidden="true">✓</span>}
    </button>;
  };
  return(<div data-interest-bar="1" style={{display:"flex",flexDirection:"column",gap:6,padding:"10px 14px",marginBottom:16,borderRadius:12,border:"1px solid "+C.border,background:C.surface}}>
    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
      <span style={{fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:.6,textTransform:"uppercase",marginRight:2}}>How they feel</span>
      {INTEREST_LEVELS.map(function(k){return chip(k,INTEREST_META[k].label,INTEREST_META[k].color,null,s[k].n,s[k].comm);})}
      {chip("none",INTEREST_NONE_LABEL,null,null,s.none.n,s.none.comm)}
      <span aria-hidden="true" style={{width:1,height:20,background:C.border,margin:"0 2px"}}/>
      {chip("booked",BOOKED_LABEL,C.text,C.booked,s.booked.n,s.booked.comm)}
      {active&&<button type="button" data-interest-clear="1" onClick={onClear} title="Show every file again"
        style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:20,cursor:"pointer",fontFamily:FONT,fontSize:12,fontWeight:800,border:"1px solid "+C.accent+"55",background:C.accentGlow,color:C.accentLight,whiteSpace:"nowrap"}}>
        Clear ✕
      </button>}
      {active&&total!=null&&<span data-interest-showing="1" style={{fontSize:11.5,color:C.accentLight,fontWeight:700}}>Showing {shown} of {total} files</span>}
    </div>
    <div data-interest-legend="1" style={{fontSize:11,color:C.textDim,lineHeight:1.5}}>
      The name's colour is how you feel — <b style={{color:C.feelGood}}>green</b> good, <b style={{color:C.feelFence}}>blue</b> on the fence, <b style={{color:C.feelBad}}>red</b> not good, plain when not set.
      {" "}<span style={{background:C.booked,color:C.text,fontWeight:700,padding:"0 5px",borderRadius:4}}>Yellow highlight</span> = booked (your switch on each file — stages never change it).
      {" "}Set both with the dot by the name.
    </div>
  </div>);
}

// "Booked leads (N)" — the Pipeline header's one-click switch (Reece, 10/10:
// "filter the pipeline with one click on 'Booked leads' so it will then show
// the pipeline based on every status that has been marked as booked"). It IS
// the Booked chip in InterestBar: both flip `filter.booked` (one source of
// truth, remembered with the interest filter). N = booked files on the board.
function BookedLeadsToggle({count,on,onToggle}){
  return(<button type="button" data-booked-toggle="1" aria-pressed={!!on} onClick={onToggle}
    title={on?"Showing booked leads only — click to show everything again":"Show only the files you've marked Booked, in every stage"}
    style={{display:"inline-flex",alignItems:"center",gap:8,padding:"8px 15px",borderRadius:9,cursor:"pointer",fontFamily:FONT,fontSize:13,fontWeight:800,whiteSpace:"nowrap",
      border:(on?"2px solid ":"1px solid ")+C.bookedEdge,margin:on?0:1,background:on?C.bookedHover:C.booked,color:C.text,boxShadow:on?"0 1px 5px rgba(11,42,74,0.18)":"none"}}>
    <span aria-hidden="true" style={{width:10,height:10,borderRadius:3,flexShrink:0,background:on?C.text:C.bookedEdge}}/>
    <span>Booked leads <span data-booked-count="1">({count||0})</span></span>
    {on&&<span aria-hidden="true">✓</span>}
  </button>);
}

// The strip over the lanes while Booked is on: "Showing booked leads only ·
// Show everything" (Show everything clears the interest filter — levels too).
function BookedStrip({filter,shown,total,onShowAll}){
  const lv=((filter&&filter.levels)||[]).map(function(k){return k==="none"?INTEREST_NONE_LABEL:INTEREST_META[k].label;});
  return(<div data-booked-strip="1" role="status" style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",padding:"9px 14px",marginBottom:12,borderRadius:10,
    background:C.booked,border:"1px solid "+C.bookedEdge,boxShadow:"inset 4px 0 0 "+C.bookedEdge,fontSize:12.5,color:C.text,fontFamily:FONT}}>
    <span style={{fontWeight:800}}>Showing booked leads only</span>
    {lv.length>0&&<span>(and only {lv.join(" / ")})</span>}
    {total!=null&&<span style={{fontWeight:600}}>— {shown} of {total} files</span>}
    <span aria-hidden="true">·</span>
    <button type="button" data-booked-show-all="1" onClick={onShowAll} title="Turn off Booked (and any interest filter) — every file again"
      style={{border:"none",background:"none",padding:0,cursor:"pointer",fontFamily:FONT,fontSize:12.5,fontWeight:800,color:C.accent,textDecoration:"underline"}}>Show everything</button>
  </div>);
}

export { InterestMenu, InterestDot, InterestBar, BookedLeadsToggle, BookedStrip };
